/**
 * One overlay scrollbar for every scroll container of the web app.
 *
 * globals.css hides the browser's scrollbars, so a scrollbar never takes room
 * from the content: a list that grows past its pane keeps its width, nothing
 * shifts when the bar appears, and tinted headers reach the edge. This module
 * draws a thin bar over the edge of the container under the pointer and of the
 * one being scrolled, and lets the user drag it. Wheel, touch and keyboard
 * scrolling stay the browser's.
 *
 * Containers that hide their bar on purpose (`scrollbar-hide`) or draw their
 * own (Radix ScrollArea viewports) are left alone. With forced colours on, the
 * browser keeps its own scrollbars and this module does nothing.
 */

const OPT_OUT_SELECTOR = '.scrollbar-hide, [data-radix-scroll-area-viewport]';
/** Same as the Radix ScrollArea's `scrollHideDelay`, so both bars fade alike. */
const HIDE_DELAY_MS = 600;
/** Leaves the bar's fade-out time to finish before its nodes are removed. */
const REMOVE_DELAY_MS = 250;
const MIN_THUMB_PX = 20;
const DEFAULT_THICKNESS_PX = 6;

type Axis = 'x' | 'y';
const AXES: Axis[] = ['x', 'y'];

export interface ThumbGeometry {
  size: number;
  offset: number;
}

/** Thumb length and offset along a track, or null when the axis does not scroll. */
export function thumbGeometry(
  scrollSize: number,
  clientSize: number,
  scrollPosition: number,
  trackSize: number,
  minThumb = MIN_THUMB_PX
): ThumbGeometry | null {
  const maxScroll = scrollSize - clientSize;
  if (maxScroll < 1 || trackSize <= 0) return null;
  const size = Math.min(trackSize, Math.max(minThumb, (trackSize * clientSize) / scrollSize));
  const progress = Math.min(1, Math.max(0, scrollPosition / maxScroll));
  return { size, offset: (trackSize - size) * progress };
}

interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface Bar {
  track: HTMLDivElement;
  thumb: HTMLDivElement;
  /** Scroll pixels per pixel of thumb travel, from the last layout. */
  ratio: number;
}

interface Entry {
  owner: Element;
  bars: Record<Axis, Bar>;
  hovered: boolean;
  dragging: boolean;
  visible: boolean;
  hideTimer: ReturnType<typeof setTimeout> | null;
  removeTimer: ReturnType<typeof setTimeout> | null;
}

export function installOverlayScrollbars(doc: Document = document): () => void {
  const win = doc.defaultView;
  if (!win || win.matchMedia?.('(forced-colors: active)').matches) return () => {};

  const layer = doc.createElement('div');
  layer.className = 'overlay-scrollbars';
  layer.setAttribute('aria-hidden', 'true');
  doc.body.append(layer);

  const entries = new Map<Element, Entry>();
  const resizeObserver =
    typeof win.ResizeObserver === 'function' ? new win.ResizeObserver(() => schedule()) : null;
  let frame: number | null = null;
  let flushing = false;
  /** Pointer moves alone only re-check the hover; scrolls and resizes re-measure. */
  let layoutDirty = false;
  let pendingPointerTarget: EventTarget | null = null;
  let lastPointerTarget: EventTarget | null = null;

  const isDocumentScroller = (element: Element) => element === doc.scrollingElement;

  function scrollsOn(element: Element, axis: Axis): boolean {
    const overflowing =
      axis === 'y'
        ? element.scrollHeight - element.clientHeight >= 1
        : element.scrollWidth - element.clientWidth >= 1;
    if (!overflowing) return false;
    const style = win!.getComputedStyle(element);
    const overflow = axis === 'y' ? style.overflowY : style.overflowX;
    if (isDocumentScroller(element)) return overflow !== 'hidden' && overflow !== 'clip';
    return overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay';
  }

  const drawsBar = (element: Element) =>
    !element.matches(OPT_OUT_SELECTOR) && (scrollsOn(element, 'y') || scrollsOn(element, 'x'));

  function nearestScroller(target: Element, axis: Axis): Element | null {
    for (let element: Element | null = target; element; element = element.parentElement) {
      if (!element.matches(OPT_OUT_SELECTOR) && scrollsOn(element, axis)) return element;
    }
    return null;
  }

  function createBar(axis: Axis, entry: () => Entry): Bar {
    const track = doc.createElement('div');
    track.className = 'overlay-scrollbar';
    track.dataset.axis = axis;
    track.dataset.state = 'hidden';
    const thumb = doc.createElement('div');
    thumb.className = 'overlay-scrollbar-thumb';
    track.append(thumb);
    layer.append(track);
    const bar: Bar = { track, thumb, ratio: 0 };
    thumb.addEventListener('pointerdown', event => startDrag(entry(), axis, bar, event));
    return bar;
  }

  function entryFor(owner: Element): Entry {
    let entry = entries.get(owner);
    if (entry) return entry;
    const created: Entry = {
      owner,
      bars: { x: createBar('x', () => created), y: createBar('y', () => created) },
      hovered: false,
      dragging: false,
      visible: false,
      hideTimer: null,
      removeTimer: null,
    };
    entry = created;
    entries.set(owner, entry);
    resizeObserver?.observe(owner);
    if (owner.firstElementChild) resizeObserver?.observe(owner.firstElementChild);
    return entry;
  }

  function remove(entry: Entry) {
    if (entry.hideTimer) clearTimeout(entry.hideTimer);
    if (entry.removeTimer) clearTimeout(entry.removeTimer);
    for (const axis of AXES) entry.bars[axis].track.remove();
    resizeObserver?.unobserve(entry.owner);
    if (entry.owner.firstElementChild) resizeObserver?.unobserve(entry.owner.firstElementChild);
    entries.delete(entry.owner);
    // The pointer may still rest on the container; check it again on the next move.
    lastPointerTarget = null;
  }

  function setVisible(entry: Entry, visible: boolean) {
    entry.visible = visible;
    for (const axis of AXES) entry.bars[axis].track.dataset.state = visible ? 'visible' : 'hidden';
    if (entry.removeTimer) clearTimeout(entry.removeTimer);
    entry.removeTimer = visible ? null : setTimeout(() => remove(entry), REMOVE_DELAY_MS);
  }

  /** Shows the bars now and hides them once the container is idle and not hovered. */
  function wake(entry: Entry) {
    if (!entry.visible) setVisible(entry, true);
    if (entry.hideTimer) clearTimeout(entry.hideTimer);
    entry.hideTimer =
      entry.hovered || entry.dragging
        ? null
        : setTimeout(() => {
            entry.hideTimer = null;
            setVisible(entry, false);
          }, HIDE_DELAY_MS);
    schedule();
  }

  function schedule(layout = true) {
    if (layout) layoutDirty = true;
    if (frame === null && !flushing) frame = win!.requestAnimationFrame(flush);
  }

  function flush() {
    frame = null;
    flushing = true;
    try {
      if (pendingPointerTarget) {
        updateHover(pendingPointerTarget);
        pendingPointerTarget = null;
      }
      if (layoutDirty) {
        layoutDirty = false;
        layoutBars();
      }
    } finally {
      flushing = false;
    }
  }

  function layoutBars() {
    const thickness =
      parseFloat(
        win!.getComputedStyle(doc.documentElement).getPropertyValue('--app-scrollbar-width')
      ) || DEFAULT_THICKNESS_PX;
    // Read every layout first, then write, so the bars cost one layout pass.
    const layouts = [...entries.values()]
      .filter(entry => entry.visible)
      .map(entry => ({ entry, layout: measure(entry.owner, thickness) }));
    for (const { entry, layout } of layouts) {
      if (!layout) {
        remove(entry);
        continue;
      }
      for (const axis of AXES) paint(entry.bars[axis], axis, layout[axis], layout.clip);
    }
  }

  function paddingBox(element: Element): Rect {
    if (isDocumentScroller(element)) {
      return { left: 0, top: 0, right: element.clientWidth, bottom: element.clientHeight };
    }
    const rect = element.getBoundingClientRect();
    const left = rect.left + element.clientLeft;
    const top = rect.top + element.clientTop;
    return { left, top, right: left + element.clientWidth, bottom: top + element.clientHeight };
  }

  /** The part of the container that is on screen, inside every clipping ancestor. */
  function visibleRect(owner: Element, box: Rect): Rect {
    const clip = { ...box };
    const intersect = (rect: Rect) => {
      clip.left = Math.max(clip.left, rect.left);
      clip.top = Math.max(clip.top, rect.top);
      clip.right = Math.min(clip.right, rect.right);
      clip.bottom = Math.min(clip.bottom, rect.bottom);
    };
    intersect({ left: 0, top: 0, right: win!.innerWidth, bottom: win!.innerHeight });
    for (let element = owner.parentElement; element; element = element.parentElement) {
      if (isDocumentScroller(element) || element === doc.body) continue;
      const style = win!.getComputedStyle(element);
      if (style.overflowX !== 'visible' || style.overflowY !== 'visible') {
        intersect(paddingBox(element));
      }
    }
    return clip;
  }

  function measure(owner: Element, thickness: number) {
    if (!owner.isConnected) return null;
    const box = paddingBox(owner);
    const scrollsY = scrollsOn(owner, 'y');
    const scrollsX = scrollsOn(owner, 'x');
    if (!scrollsX && !scrollsY) return null;
    const width = box.right - box.left;
    const height = box.bottom - box.top;
    const yTrack = height - (scrollsX ? thickness : 0);
    const xTrack = width - (scrollsY ? thickness : 0);
    return {
      clip: visibleRect(owner, box),
      y: scrollsY
        ? {
            rect: { left: box.right - thickness, top: box.top, width: thickness, height: yTrack },
            geometry: thumbGeometry(
              owner.scrollHeight,
              owner.clientHeight,
              owner.scrollTop,
              yTrack
            ),
            maxScroll: owner.scrollHeight - owner.clientHeight,
          }
        : null,
      x: scrollsX
        ? {
            rect: { left: box.left, top: box.bottom - thickness, width: xTrack, height: thickness },
            geometry: thumbGeometry(owner.scrollWidth, owner.clientWidth, owner.scrollLeft, xTrack),
            maxScroll: owner.scrollWidth - owner.clientWidth,
          }
        : null,
    };
  }

  function paint(
    bar: Bar,
    axis: Axis,
    layout: {
      rect: { left: number; top: number; width: number; height: number };
      geometry: ThumbGeometry | null;
      maxScroll: number;
    } | null,
    clip: Rect
  ) {
    const { track, thumb } = bar;
    if (!layout?.geometry) {
      track.style.display = 'none';
      return;
    }
    const { rect, geometry } = layout;
    track.style.display = '';
    track.style.left = `${rect.left}px`;
    track.style.top = `${rect.top}px`;
    track.style.width = `${rect.width}px`;
    track.style.height = `${rect.height}px`;
    const insets = [
      clip.top - rect.top,
      rect.left + rect.width - clip.right,
      rect.top + rect.height - clip.bottom,
      clip.left - rect.left,
    ].map(inset => `${Math.max(0, inset)}px`);
    track.style.clipPath = `inset(${insets.join(' ')})`;
    if (axis === 'y') {
      thumb.style.width = '100%';
      thumb.style.height = `${geometry.size}px`;
      thumb.style.transform = `translateY(${geometry.offset}px)`;
    } else {
      thumb.style.width = `${geometry.size}px`;
      thumb.style.height = '100%';
      thumb.style.transform = `translateX(${geometry.offset}px)`;
    }
    const travel = (axis === 'y' ? rect.height : rect.width) - geometry.size;
    bar.ratio = travel > 0 ? layout.maxScroll / travel : 0;
  }

  function startDrag(entry: Entry, axis: Axis, bar: Bar, event: PointerEvent) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const { owner } = entry;
    const start = axis === 'y' ? event.clientY : event.clientX;
    const startScroll = axis === 'y' ? owner.scrollTop : owner.scrollLeft;
    entry.dragging = true;
    bar.track.dataset.dragging = '';
    wake(entry);

    const move = (moveEvent: PointerEvent) => {
      const delta = ((axis === 'y' ? moveEvent.clientY : moveEvent.clientX) - start) * bar.ratio;
      if (axis === 'y') owner.scrollTop = startScroll + delta;
      else owner.scrollLeft = startScroll + delta;
      schedule();
    };
    const end = () => {
      win!.removeEventListener('pointermove', move);
      win!.removeEventListener('pointerup', end);
      win!.removeEventListener('pointercancel', end);
      entry.dragging = false;
      delete bar.track.dataset.dragging;
      wake(entry);
    };
    win!.addEventListener('pointermove', move);
    win!.addEventListener('pointerup', end);
    win!.addEventListener('pointercancel', end);
  }

  function updateHover(target: EventTarget) {
    // The pointer is on one of the bars: its container stays hovered.
    if (target instanceof win!.Node && layer.contains(target)) return;
    if (target === lastPointerTarget) return;
    lastPointerTarget = target;
    const hovered = new Set<Element>();
    if (target instanceof win!.Element) {
      for (const axis of AXES) {
        const owner = nearestScroller(target, axis);
        if (owner) hovered.add(owner);
      }
    }
    for (const entry of entries.values()) {
      if (entry.hovered && !hovered.has(entry.owner)) {
        entry.hovered = false;
        wake(entry);
      }
    }
    for (const owner of hovered) {
      const entry = entryFor(owner);
      entry.hovered = true;
      wake(entry);
    }
  }

  const onScroll = (event: Event) => {
    const target = event.target === doc ? doc.scrollingElement : event.target;
    if (target instanceof win.Element && drawsBar(target)) wake(entryFor(target));
    // Any scroll can move the bars of the containers around it.
    else if (entries.size > 0) schedule();
  };

  const onPointerMove = (event: PointerEvent) => {
    if (event.pointerType === 'touch') return;
    pendingPointerTarget = event.target;
    schedule(false);
  };

  const onPointerOut = (event: PointerEvent) => {
    // Leaving the window: nothing is hovered any more.
    if (event.relatedTarget === null) {
      pendingPointerTarget = doc;
      schedule(false);
    }
  };

  const onResize = () => schedule();

  doc.addEventListener('scroll', onScroll, { capture: true, passive: true });
  doc.addEventListener('pointermove', onPointerMove, { capture: true, passive: true });
  doc.addEventListener('pointerout', onPointerOut, { capture: true, passive: true });
  win.addEventListener('resize', onResize, { passive: true });

  return () => {
    doc.removeEventListener('scroll', onScroll, { capture: true });
    doc.removeEventListener('pointermove', onPointerMove, { capture: true });
    doc.removeEventListener('pointerout', onPointerOut, { capture: true });
    win.removeEventListener('resize', onResize);
    if (frame !== null) win.cancelAnimationFrame(frame);
    for (const entry of [...entries.values()]) remove(entry);
    resizeObserver?.disconnect();
    layer.remove();
  };
}
